import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ListChecks, Paperclip } from "lucide-react";
import { receipts, ReceiptListItem, ReceiptStatus, ReceiptType } from "../api/client";
import StatusPill from "../components/b/StatusPill";
import { formatReceiptDate } from "../receiptDate";
import { AttachmentFilter, readView, toApiFilters } from "./receiptFilters";

const STATUS_OPTIONS: { value: "" | ReceiptStatus; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Needs review" },
  { value: "reviewed", label: "Reviewed" },
  { value: "error", label: "Error" },
];

const TYPE_OPTIONS: { value: "" | ReceiptType; label: string }[] = [
  { value: "", label: "All types" },
  { value: "receipt", label: "Receipt" },
  { value: "bill", label: "Bill" },
  { value: "income", label: "Income" },
];

const TYPE_LABEL: Record<ReceiptType, string> = {
  receipt: "Receipt",
  bill: "Bill",
  income: "Income",
};

const ATTACHMENT_OPTIONS: { value: AttachmentFilter; label: string }[] = [
  { value: "", label: "Any" },
  { value: "yes", label: "With attachments" },
  { value: "no", label: "No attachments" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatCents(cents?: number | null, currency?: string | null): string {
  if (cents == null) return "—";
  const value = cents / 100;
  try {
    return value.toLocaleString("en-US", { style: "currency", currency: currency || "USD" });
  } catch {
    return value.toFixed(2);
  }
}

export default function Receipts() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const view = readView(params);
  const pageSize = Number(params.get("size")) || 25;
  const page = Math.max(1, Number(params.get("page")) || 1); // 1-based for the UI

  const [items, setItems] = useState<ReceiptListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null,
  );
  const [search, setSearch] = useState(view.q);
  // Back/forward changes ?q=: keep the box in step.
  useEffect(() => {
    setSearch((current) => (current.trim() === view.q ? current : view.q));
  }, [view.q]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  /** Change URL params; any filter change snaps back to page 1. */
  function update(changes: Record<string, string | number>, keepPage = false) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    }
    if (!keepPage) next.delete("page");
    setParams(next, { replace: true });
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await receipts.listPaginated({
        ...toApiFilters(view),
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(data.items);
      setTotal(data.total);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load receipts");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  // Search runs server-side; wait for a pause in typing.
  useEffect(() => {
    if (search === view.q) return;
    const t = setTimeout(() => update({ q: search.trim() }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstShown = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = Math.min(page * pageSize, total);
  const allSelected = items.length > 0 && items.every((r) => selected.has(r.id));
  const detailSearch = useMemo(() => {
    const p = new URLSearchParams(params);
    return p.toString() ? `?${p}` : "";
  }, [params]);

  function goTo(p: number) {
    update({ page: Math.min(totalPages, Math.max(1, p)) }, true);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkMark(status: ReceiptStatus) {
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await receipts.bulkStatus([...selected], status);
      setNotice(`Marked ${res.updated} receipt${res.updated === 1 ? "" : "s"} ${status === "reviewed" ? "reviewed" : "as needing review"}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  /** Open the first receipt still needing review, within the current filters. */
  async function startReview() {
    setBusy(true);
    setError(null);
    try {
      const queue = await receipts.queue(toApiFilters(view));
      const first = queue.find((q) => q.status === "pending");
      if (!first) {
        setNotice("Nothing left to review in this list.");
        return;
      }
      navigate(`/app/receipts/${first.id}${detailSearch}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start review");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Ledger</h1>
        <button className="btn btn-primary" onClick={startReview} disabled={busy}>
          <ListChecks size={16} /> Review
        </button>
        <p className="page-sub">Every receipt, bill and payment received, from your mailboxes and uploads.</p>
      </div>

      <div className="filter-bar">
        <label style={{ flex: "0 0 200px" }}>
          Status
          <select value={view.status} onChange={(e) => update({ status: e.target.value })}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: "0 0 180px" }}>
          Type
          <select value={view.type} onChange={(e) => update({ type: e.target.value })}>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: "0 0 180px" }}>
          Attachments
          <select value={view.att} onChange={(e) => update({ att: e.target.value })}>
            {ATTACHMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={{ flex: 1 }}>
          Search
          <input
            type="search"
            placeholder="Vendor, sender, subject or invoice #"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {error && <div className="page-error">{error}</div>}
      {notice && <div className="page-toast">{notice}</div>}

      {selected.size > 0 && (
        <div className="bulk-bar">
          <span>
            <strong>{selected.size}</strong> selected
          </span>
          <button className="btn btn-primary btn-sm" onClick={() => bulkMark("reviewed")} disabled={busy}>
            Mark reviewed
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => bulkMark("pending")} disabled={busy}>
            Mark unreviewed
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())} disabled={busy}>
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="page-empty">
          <p>No receipts match these filters.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th className="check-col">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((r) => r.id)))}
                />
              </th>
              <th>Date</th>
              <th>Vendor</th>
              <th>Subject</th>
              <th aria-label="Attachments">
                <Paperclip size={14} />
              </th>
              <th style={{ textAlign: "right" }}>Amount</th>
              <th>Type</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => {
              const attach = (r as any).attachment_file_ids;
              const attachCount = Array.isArray(attach) ? attach.length : 0;
              return (
                <tr
                  key={r.id}
                  data-tour={r.is_sample && items.findIndex((x) => x.is_sample) === i ? "sample-row" : undefined}
                  className={"clickable" + (selected.has(r.id) ? " selected" : "")}
                  onClick={() => navigate(`/app/receipts/${r.id}${detailSearch}`)}
                >
                  <td className="check-col" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.vendor_name || r.sender || "receipt"}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                  <td>{formatReceiptDate(r)}</td>
                  <td>
                    {r.vendor_name || r.sender || "Unknown"}
                    {r.is_sample && <span className="sample-chip">Sample</span>}
                  </td>
                  <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.subject || "(no subject)"}
                  </td>
                  <td>{attachCount > 0 ? attachCount : ""}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {formatCents(r.amount_cents, r.currency)}
                  </td>
                  <td>
                    <StatusPill status={r.receipt_type} label={TYPE_LABEL[r.receipt_type] ?? r.receipt_type} />
                  </td>
                  <td><StatusPill status={r.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {total > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 16,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, color: "var(--muted, #666)" }}>
            Showing <strong>{firstShown}–{lastShown}</strong> of{" "}
            <strong>{total}</strong>
          </div>
          <div className="pager">
            <label className="inline-label" style={{ marginRight: 8 }}>
              Rows
              <select value={pageSize} onChange={(e) => update({ size: Number(e.target.value) })}>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goTo(1)}
              disabled={page <= 1}
            >
              «
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goTo(page - 1)}
              disabled={page <= 1}
            >
              ‹
            </button>
            <span className="inline-label" style={{ display: "inline-flex", gap: 6 }}>
              Page
              <input
                type="number"
                min={1}
                max={totalPages}
                value={page}
                onChange={(e) => goTo(Number(e.target.value))}
                style={{ width: 56, height: 30, padding: "0 8px" }}
              />
              of {totalPages}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goTo(page + 1)}
              disabled={page >= totalPages}
            >
              ›
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goTo(totalPages)}
              disabled={page >= totalPages}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
